import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { GeoSetTargetable } from '@feathr/blackbox';
import { TargetableClass } from '@feathr/blackbox';
import { showError, toast } from '@feathr/components';
import type { IGeoFilterSetData } from '@feathr/extender/components';
import { useModals } from '@feathr/extender/components';
import { useStore } from '@feathr/extender/state';
import { useAccount } from '@feathr/report_components';

/**
 * Return type for the useGeoFiltersPage hook.
 */
interface IUseGeoFiltersPageReturn {
  /** The currently selected geo set for editing, null when creating */
  selectedGeoSet: GeoSetTargetable | null;
  /** The geo set selected to be set as default, null when not setting default */
  geoSetForDefault: GeoSetTargetable | null;
  /** The current default geo set ID from account settings */
  defaultGeoSetId: string | undefined;
  /** Whether a save operation is in progress for setting default */
  isSavingDefault: boolean;
  /** Modal state handlers from useModals */
  modals: ReturnType<typeof useModals>;
  /** Handler to close the modal and reset selection */
  handleCloseModal: () => void;
  /** Handler to save a geo set (creates or edits based on context) */
  handleSaveGeoSet: (data: IGeoFilterSetData) => Promise<void>;
  /** Handler to open the create modal */
  handleOpenCreate: () => void;
  /** Handler to open the edit modal with a targetable */
  handleOpenEdit: (targetable: GeoSetTargetable) => void;
  /** Handler to set a geo set as default (opens modal) */
  handleSetDefault: (targetable: GeoSetTargetable) => void;
  /** Handler to confirm setting a geo set as default */
  handleConfirmSetDefault: () => Promise<void>;
}

/**
 * Custom hook that manages all state and logic for the GeoFiltersPage component.
 *
 * Handles:
 * - Selected geo set state for editing
 * - Modal open/close operations
 * - Create and edit geo set operations
 * - Setting default geo sets
 *
 * @returns Object containing state values and handlers
 *
 * @example
 * ```tsx
 * const {
 *   selectedGeoSet,
 *   modals,
 *   handleOpenCreate,
 *   handleSaveGeoSet,
 * } = useGeoFiltersPage();
 * ```
 */
export function useGeoFiltersPage(): IUseGeoFiltersPageReturn {
  const { t } = useTranslation();
  const { Targetables } = useStore();
  const account = useAccount();
  const modals = useModals();

  const [selectedGeoSet, setSelectedGeoSet] = useState<GeoSetTargetable | null>(null);
  const [geoSetForDefault, setGeoSetForDefault] = useState<GeoSetTargetable | null>(null);
  const [isSavingDefault, setIsSavingDefault] = useState(false);

  const defaultGeoSetId = account.getSetting('default_geo_set');

  const handleCloseModal = useCallback((): void => {
    setSelectedGeoSet(null);
    modals.editCreate.close();
  }, [modals.editCreate]);

  const handleCreateGeoSet = useCallback(
    async (data: IGeoFilterSetData): Promise<void> => {
      const geoSet = Targetables.create({
        _cls: TargetableClass.geo_set,
        name: data.name,
        included_geo_filters: data.included_geo_filters,
        excluded_geo_filters: data.excluded_geo_filters,
      });

      await Targetables.add(geoSet);

      if (data.is_default) {
        account.setSetting('default_geo_set', geoSet.id);
        await account.patchDirty();
      }

      toast(t('Geofilter set "{{- name}}" created successfully', { name: data.name }), {
        type: 'success',
      });

      handleCloseModal();
    },
    [Targetables, account, t, handleCloseModal],
  );

  const handleEditGeoSet = useCallback(
    async (data: IGeoFilterSetData): Promise<void> => {
      if (!selectedGeoSet) {
        return;
      }

      selectedGeoSet.set({
        name: data.name,
        included_geo_filters: data.included_geo_filters,
        excluded_geo_filters: data.excluded_geo_filters,
      });

      await selectedGeoSet.save();

      const wasDefault = selectedGeoSet.isDefault(defaultGeoSetId);
      if (data.is_default && !wasDefault) {
        account.setSetting('default_geo_set', selectedGeoSet.id);
        await account.patchDirty();
      } else if (!data.is_default && wasDefault) {
        account.setSetting('default_geo_set', undefined);
        await account.patchDirty();
      }

      toast(t('Geofilter set "{{- name}}" updated successfully', { name: data.name }), {
        type: 'success',
      });

      handleCloseModal();
    },
    [selectedGeoSet, defaultGeoSetId, account, t, handleCloseModal],
  );

  const handleSaveGeoSet = useCallback(
    async (data: IGeoFilterSetData): Promise<void> => {
      try {
        if (modals.editCreate.context === 'create') {
          await handleCreateGeoSet(data);
        } else {
          await handleEditGeoSet(data);
        }
      } catch (error) {
        showError(error, t);
        const action = modals.editCreate.context === 'create' ? 'create' : 'update';
        toast(t(`Failed to ${action} geofilter set. Please try again.`), {
          type: 'error',
        });
      }
    },
    [modals.editCreate.context, handleCreateGeoSet, handleEditGeoSet, t],
  );

  const handleOpenCreate = useCallback((): void => {
    setSelectedGeoSet(null);
    modals.editCreate.handleOpen('create')();
  }, [modals.editCreate]);

  const handleOpenEdit = useCallback(
    (targetable: GeoSetTargetable): void => {
      setSelectedGeoSet(targetable);
      modals.editCreate.handleOpen('edit')();
    },
    [modals.editCreate],
  );

  const handleSetDefault = useCallback(
    (targetable: GeoSetTargetable): void => {
      setGeoSetForDefault(targetable);
      modals.defaultGeoSet.open();
    },
    [modals.defaultGeoSet],
  );

  const handleConfirmSetDefault = useCallback(async (): Promise<void> => {
    if (!geoSetForDefault) {
      return;
    }

    setIsSavingDefault(true);

    try {
      account.setSetting('default_geo_set', geoSetForDefault.id);
      await account.patchDirty();

      toast(
        t('Geofilter set "{{- name}}" is now the default', { name: geoSetForDefault.get('name') }),
        { type: 'success' },
      );

      modals.defaultGeoSet.close();
      setGeoSetForDefault(null);
    } catch (error) {
      showError(error, t);
      toast(t('Failed to set default geofilter set. Please try again.'), {
        type: 'error',
      });
    } finally {
      setIsSavingDefault(false);
    }
  }, [geoSetForDefault, account, modals.defaultGeoSet, t]);

  return {
    selectedGeoSet,
    geoSetForDefault,
    defaultGeoSetId,
    isSavingDefault,
    modals,
    handleCloseModal,
    handleSaveGeoSet,
    handleOpenCreate,
    handleOpenEdit,
    handleSetDefault,
    handleConfirmSetDefault,
  };
}
